import React from 'react';
import { useGlobalSetting } from '@/hooks/useGlobalSettings';
import { useLanguage } from '@/contexts/LanguageContext';

interface LogoSizes {
  mobile: { height: string };
  tablet: { height: string };
  desktop: { height: string };
}

interface LogoSettings {
  logoUrl: string;
  siteName: string;
  animationEnabled: boolean;
  hoverEffects: boolean;
  transitionDuration: string;
}

interface UseLogoSettingsProps {
  variant?: 'light' | 'dark' | 'auto';
  position?: 'header' | 'footer' | 'custom';
  fallbackToDefault?: boolean;
}

export const useLogoSettings = ({
  variant = 'auto',
  position = 'header',
  fallbackToDefault = true
}: UseLogoSettingsProps = {}) => {
  const { language } = useLanguage();
  
  // الشعارات الأساسية
  const logoArLight = useGlobalSetting<string>('branding.logo_ar_light', '');
  const logoArDark = useGlobalSetting<string>('branding.logo_ar_dark', '');
  const logoEnLight = useGlobalSetting<string>('branding.logo_en_light', '');
  const logoEnDark = useGlobalSetting<string>('branding.logo_en_dark', '');
  
  // أحجام الشعارات
  const headerSizes = useGlobalSetting<LogoSizes>('branding.header_logo_sizes', {
    mobile: { height: '24px' },
    tablet: { height: '28px' },
    desktop: { height: '32px' }
  });
  
  const footerSizes = useGlobalSetting<LogoSizes>('branding.footer_logo_sizes', {
    mobile: { height: '20px' },
    tablet: { height: '24px' },
    desktop: { height: '28px' }
  });

  // إعداد الحد الأقصى
  const maxLogoHeight = useGlobalSetting<string>('branding.max_logo_height', '75px');
  
  // الإعدادات العامة
  const animationEnabled = useGlobalSetting<boolean>('branding.logo_animation_enabled', true);
  const hoverEffects = useGlobalSetting<boolean>('branding.logo_hover_effects', true);
  const transitionDuration = useGlobalSetting<string>('branding.logo_transition_duration', '300ms');
  
  // أسماء الموقع
  const siteNameAr = useGlobalSetting<string>('branding.site_name_ar', 'أباسك');
  const siteNameEn = useGlobalSetting<string>('branding.site_name_en', 'ABASC HOLDING');
  
  // الشعارات الافتراضية (fallback)
  const defaultLightLogo = '/op-uploads/434e5d1e-e206-4ef4-b7e3-fd576e9d5868.png';
  const defaultDarkLogo = '/op-uploads/c47d1825-cc5b-4807-bb90-e3fcbe9f8c45.png';

  /**
   * منطق اختيار الشعار المناسب
   */
  const getLogoUrl = (targetVariant: 'light' | 'dark'): string => {
    // الشعار المفضل حسب اللغة والوضع
    const preferredLogo = language === 'ar' 
      ? (targetVariant === 'light' ? logoArLight : logoArDark)
      : (targetVariant === 'light' ? logoEnLight : logoEnDark);
    
    if (preferredLogo) return preferredLogo;
    
    // شعار اللغة البديلة بنفس الوضع
    const alternateLangLogo = language === 'ar'
      ? (targetVariant === 'light' ? logoEnLight : logoEnDark)
      : (targetVariant === 'light' ? logoArLight : logoArDark);
    
    if (alternateLangLogo) return alternateLangLogo;
    
    // شعار الوضع البديل بنفس اللغة
    const alternateModeLogo = language === 'ar'
      ? (targetVariant === 'light' ? logoArDark : logoArLight)
      : (targetVariant === 'light' ? logoEnDark : logoEnLight);
    
    if (alternateModeLogo) return alternateModeLogo;
    
    // الشعار الافتراضي
    if (fallbackToDefault) {
      return targetVariant === 'light' ? defaultLightLogo : defaultDarkLogo;
    }
    
    return '';
  };

  /**
   * تحديد الوضع المناسب للشعار
   */
  const resolveVariant = (requestedVariant: 'light' | 'dark' | 'auto'): 'light' | 'dark' => {
    if (requestedVariant === 'auto') {
      // يمكن إضافة منطق للكشف عن الوضع المظلم/الفاتح هنا
      return 'light'; // افتراضي
    }
    return requestedVariant;
  };

  /**
   * الحصول على أحجام الشعار حسب الموضع
   */
  const getSizes = (): LogoSizes => {
    return position === 'footer' ? footerSizes : headerSizes;
  };

  /**
   * الحصول على اسم الموقع
   */
  const getSiteName = (): string => {
    return language === 'ar' ? siteNameAr : siteNameEn;
  };

  /**
   * دالة لتحديد الحد الأقصى لحجم الشعار
   */
  const clampLogoSize = (size: string): string => {
    const maxHeight = parseInt(maxLogoHeight);
    const sizeValue = parseInt(size);
    
    if (sizeValue > maxHeight) {
      return `${maxHeight}px`;
    }
    
    return size;
  };

  /**
   * الحصول على classes CSS للأحجام
   */
  const getSizeClasses = (customSize?: string): string => {
    return `w-auto object-contain`;
  };

  /**
   * الحصول على classes CSS للتأثيرات
   */
  const effectClasses = React.useMemo((): string => {
    const baseClasses = 'block';
    const transitionClass = animationEnabled ? 'transition-transform duration-200 ease-out' : '';
    
    return [baseClasses, transitionClass].filter(Boolean).join(' ');
  }, [animationEnabled]);

  const getEffectClasses = () => effectClasses;

  // الواجهة الرئيسية
  const resolvedVariant = resolveVariant(variant);
  const logoUrl = getLogoUrl(resolvedVariant);
  const siteName = getSiteName();

  const logoSettings: LogoSettings = {
    logoUrl,
    siteName,
    animationEnabled,
    hoverEffects,
    transitionDuration
  };

  return {
    // البيانات الأساسية
    logoUrl,
    siteName,
    
    // الإعدادات
    animationEnabled,
    hoverEffects,
    transitionDuration,
    
    // الأحجام
    sizes: getSizes(),
    
    // الدوال المساعدة
    getSizeClasses,
    getEffectClasses,
    clampLogoSize,
    getLogoUrl: (targetVariant: 'light' | 'dark') => getLogoUrl(targetVariant),
    
    // البيانات الكاملة
    logoSettings,
    
    // معلومات إضافية
    hasLogo: Boolean(logoUrl),
    currentVariant: resolvedVariant,
    currentLanguage: language,
    position
  };
};

/**
 * Hook مبسط للحصول على الشعار فقط
 */
export const useLogo = (variant: 'light' | 'dark' | 'auto' = 'auto') => {
  const { logoUrl, siteName, hasLogo } = useLogoSettings({ variant });
  
  return {
    logoUrl,
    siteName, 
    hasLogo
  };
};

/**
 * Hook للحصول على أحجام الشعار
 */
export const useLogoSizes = (position: 'header' | 'footer' = 'header') => {
  const { sizes, getSizeClasses } = useLogoSettings({ position });
  
  return {
    sizes,
    getSizeClasses
  };
};